<template>
  <dialog
    ref="dialog"
    @close="onCloseHandler"
    @cancel="onCancelHandler"
    v-on="{
      click: enableModalCancelOnClickAway ? clickAwayHandler : null,
      keydown: enableModalCancelOnEscapeKey ? null : handleKeyDown,
    }"
    :class="[isModal ? modalCssClasses : nonModalCssClasses ]">
    <div class="relative">
      <div
        v-if="showCancelButton"
        class="pt-8">
        <div class="absolute -top-4 -right-4">
          <ButtonComponent
            :label="null"
            variant="tertiary"
            @custom-click="cancel">
            <template #icon>
              <CrossIcon title="Close dialog" />
            </template>
          </ButtonComponent>
        </div>
      </div>
      <slot
        :cancel="cancel"
        :close="close">
      </slot>
    </div>
  </dialog>
</template>
<script>
import ButtonComponent from './ButtonComponent.vue'
import CrossIcon from './icons/CrossIcon.vue'

/** A modal dialog temporarily interrupts a user's workflow, focuses user attention and requires immediate acknowledgment or action. A non-modal dialog provides additional information or options, without blocking their workflow. */
export default {
  name: 'DialogComponent',
  components: {
    ButtonComponent,
    CrossIcon,
  },
  props: {
    /** Indicates opened (true) or closed (false) state. */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /** Mode of dialog; modal (true) or non-modal (false). */
    isModal: {
      type: Boolean,
      default: true,
    },
    /** Display cancel button inside dialog, top right corner.  */
    showCancelButton: {
      type: Boolean,
      default: true,
    },
    /** Escape key press cancels the modal. Recommended native behaviour. Modal mode only. */
    cancelModalOnEscape: {
      type: Boolean,
      default: true,
    },
    /** Clicking outside of modal cancels the modal. Modal mode only. */
    cancelModalOnClickAway: {
      type: Boolean,
      default: true,
    },
    /** Override dialog modal mode styles with css classes. Modal mode only. */
    modalCssClasses: {
      type: String,
      default: 'p-4 sm:p-8 rounded-sm bg-white shadow-lg modal:fixed top-0 right-0 bottom-0 left-0 m-auto overflow-auto w-fit h-fit max-w-[calc(100%-(1rem*2))] sm:max-w-[calc(75%-(2rem*2))] xl:max-w-[calc(50%-(2rem*2))] opacity-0 -translate-y-8 motion-safe:transition-[overlay_display_opacity_transform] transition-discrete duration-300 ease-in open:opacity-100 open:translate-y-0 starting:open:opacity-0 starting:open:translate-y-8 backdrop:bg-royal-blue-160/90 backdrop:opacity-0 motion-safe:backdrop:transition-[overlay_display_opacity] backdrop:duration-300 backdrop:ease-in backdrop:transition-discrete open:backdrop:opacity-100 starting:open:backdrop:opacity-0',
    },
    /** Override dialog non-modal mode styles with css classes. Non-modal mode only. */
    nonModalCssClasses: {
      type: String,
      default: 'p-4 sm:p-8 rounded-sm bg-white shadow-lg absolute inset-auto m-0 w-auto overflow-visible opacity-0 scale-95 motion-safe:transition-[overlay_display_opacity_scale] transition-discrete duration-250 ease-in open:opacity-100 open:scale-100 starting:open:opacity-0 starting:open:scale-95',
    },
  },
  watch: {
    modelValue: {
      handler(newVal) {
        this.$nextTick(() => {
          if (newVal) {
            this.open()
          }
          else if (this.$refs.dialog?.open) {
            this.close()
          }
        })
      },
      immediate: true,
    },
  },
  methods: {
    open() {
      const dialog = this.$refs.dialog
      if (this.isModal) {
        dialog.showModal()
      }
      else {
        dialog.show()
      }
      this.$emit('open')
    },
    close() {
      this.$refs.dialog.close()
    },
    onCloseHandler(e) {
      this.$emit('close', e)
      this.$emit('update:modelValue', false)
    },
    onCancelHandler(e) {
      this.$emit('cancel', e)
    },
    cancel(e) {
      this.onCancelHandler(e)
      this.close()
    },
    clickAwayHandler(e) {
      const { clientX, clientY, target, currentTarget } = e
      if (target !== currentTarget) return // .self modifider incompatible with v-on object syntanx
      const { left, right, top, bottom } = this.$refs.dialog.getBoundingClientRect()
      const clickedOutside = clientX < left || clientX > right || clientY < top || clientY > bottom
      if (clickedOutside) {
        this.cancel(e)
      }
    },
    handleKeyDown(e) {
      if (e.key === 'Escape') e.preventDefault() // prevent native dialog escape key event
    },
  },
  computed: {
    enableModalCancelOnEscapeKey() {
      if (!this.isModal) return true // Do not bind redundant event listener when non-modal
      return this.cancelModalOnEscape
    },
    enableModalCancelOnClickAway() {
      return this.isModal && this.cancelModalOnClickAway
    },
  },
  emits: ['open', 'close', 'cancel', 'update:modelValue'],
}
</script>
